import React, { useState, useEffect, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Layout from '../../components/Layout';
import { 
    TextField, Button, Grid, Box, Dialog, DialogTitle,
    DialogContent, DialogActions, Snackbar, Alert,
    CircularProgress, FormControl, InputLabel, Select, MenuItem,
    Typography, IconButton, InputAdornment, Menu,
    List, ListItem, ListItemText, Divider, Chip, Tooltip,
    FormControlLabel, Switch
} from '@mui/material';
import axios from 'axios';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import LockResetIcon from '@mui/icons-material/LockReset';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningIcon from '@mui/icons-material/Warning';
import BugReportIcon from '@mui/icons-material/BugReport';

const PartnerCustomers = () => {
    // state hooks
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [gridHeight, setGridHeight] = useState(400);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [creating, setCreating] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('error');
    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [loginPassword, setLoginPassword] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    
    // Partner verification status
    const [partnerInfo, setPartnerInfo] = useState(null);
    const [loadingPartnerInfo, setLoadingPartnerInfo] = useState(true);
    
    // New state for usage info dialog
    const [usageDialogOpen, setUsageDialogOpen] = useState(false);
    const [selectedCustomerUsage, setSelectedCustomerUsage] = useState(null);

    // New state for reset password functionality
    const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);
    const [resetPasswordResult, setResetPasswordResult] = useState(null);

    // New state for debug mode functionality
    const [togglingDebugMode, setTogglingDebugMode] = useState(false);

    // Form state for creating customer
    const [form, setForm] = useState({
        organisationName: '',
        firstName: '',
        lastName: '',
        password: '',
        freeTrialEligible: true
    });

    const filterRef = useRef(null);

    // effect hooks
    useEffect(() => {
        fetchData();
        fetchPartnerInfo();
    }, []);
    
    useEffect(() => {
        if (filterRef.current) {
            const formHeight = filterRef.current?.getBoundingClientRect()?.height || 100;
            const viewportHeight = window.innerHeight;
            const calculatedHeight = viewportHeight - formHeight - 200;
            
            setGridHeight(calculatedHeight);
        }
    }, []);

    const fetchData = () => {
        setLoading(true);

        axios.get('/api/partners/customers')
        .then(result => {
            setRows(result.data || []);
        })
        .catch(error => {
            console.error('Error fetching data:', error);
            setAlertMessage('Error fetching customers');
            setAlertSeverity('error');
            setShowAlert(true);
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const fetchPartnerInfo = async () => {
        setLoadingPartnerInfo(true);
        try {
            const response = await axios.get('/api/partners/current-partner-info');
            setPartnerInfo(response.data);
        } catch (error) {
            console.error('Error fetching partner info:', error);
            setAlertMessage('Error fetching partner verification status');
            setAlertSeverity('error');
            setShowAlert(true);
        } finally {
            setLoadingPartnerInfo(false);
        }
    };

    const fetchPlans = () => {
        setLoadingPlans(true);
        
        // Fetch both annual and non-annual plans
        Promise.all([
            axios.get('/api/plans', { params: { IsAnnual: false } }),
            axios.get('/api/plans', { params: { IsAnnual: true } })
        ])
        .then(([monthlyResponse, annualResponse]) => {
            const allPlans = [...monthlyResponse.data, ...annualResponse.data];
            setPlans(allPlans);
        })
        .catch(error => {
            console.error('Error fetching plans:', error);
            setAlertMessage('Error fetching plans');
            setAlertSeverity('error');
            setShowAlert(true);
        })
        .finally(() => {
            setLoadingPlans(false);
        });
    };

    const handleCreateCustomer = () => {
        // Check if partner is verified before allowing customer creation
        if (!partnerInfo?.isAuthorised) {
            setAlertMessage('You must be verified by your referring partner before you can create customer accounts. Please contact your referring partner to get verified.');
            setAlertSeverity('warning');
            setShowAlert(true);
            return;
        }
        
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        setForm({
            organisationName: '',
            firstName: '',
            lastName: '',
            password: '',
            freeTrialEligible: true
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitCreate = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (!form.organisationName || !form.firstName || !form.lastName || !form.password) {
            setAlertMessage('All fields are required');
            setAlertSeverity('error');
            setShowAlert(true);
            return;
        }

        setCreating(true);

        try {
            const response = await axios.post('/api/partners/customers', form);
            
            setAlertMessage(`Customer "${response.data.customer.name}" created successfully. Username: ${response.data.user.username}`);
            setAlertSeverity('success');
            setShowAlert(true);
            
            handleCloseCreateDialog();
            fetchData(); // Refresh the list
        } catch (error) {
            console.error('Error creating customer:', error);
            setAlertMessage(error.response?.data?.error || 'Error creating customer');
            setAlertSeverity('error');
            setShowAlert(true);
        } finally {
            setCreating(false);
        }
    };

    const handleLoginClick = (customer) => {
        console.log('Login clicked for customer:', customer);
        
        const loginStatus = getLoginStatus(customer);
        if (!loginStatus.canLogin) {
            setAlertMessage('Cannot login to this account. The partner user has been deleted by the customer and access has been revoked.');
            setAlertSeverity('warning');
            setShowAlert(true);
            return;
        }
        
        setSelectedCustomer(customer);
        setLoginPassword('');
        setLoginDialogOpen(true);
    };

    const handleCloseLoginDialog = () => {
        setLoginDialogOpen(false);
        setSelectedCustomer(null);
        setLoginPassword('');
    };

    const handleLogin = async () => {
        if (!loginPassword) {
            setAlertMessage('Password is required');
            setAlertSeverity('error');
            setShowAlert(true);
            return;
        }

        setLoggingIn(true);

        try {
            const response = await axios.post('/api/partners/customer-login', {
                customerId: selectedCustomer.id,
                password: loginPassword
            });

            if (response.data.success) {
                // Store the new tokens
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('refreshToken', response.data.refreshToken);
                
                // Redirect to client admin dashboard
                window.location.href = '/client-admin';
            }
        } catch (error) {
            console.error('Error logging in:', error);
            setAlertMessage(error.response?.data?.error || 'Login failed');
            setAlertSeverity('error');
            setShowAlert(true);
        } finally {
            setLoggingIn(false);
        }
    };

    const generateUsername = (customer) => {
        // Find the partner user for this customer
        const partnerUser = customer.users?.find(u => u.is_partner_user);
        return partnerUser?.email || 'N/A';
    };

    const isPartnerUserDeleted = (customer) => {
        // Check if there's no partner user or if the partner user is deleted
        const partnerUser = customer.users?.find(u => u.is_partner_user);
        return !partnerUser || partnerUser.deleted;
    };

    const getLoginStatus = (customer) => {
        if (isPartnerUserDeleted(customer)) {
            return {
                canLogin: false,
                status: 'deleted',
                message: 'Partner user deleted - cannot login'
            };
        }
        return {
            canLogin: true,
            status: 'active',
            message: 'Can login'
        };
    };

    const handleMenuClick = (event, customer) => {
        console.log('Menu clicked for customer:', customer);
        setMenuAnchorEl(event.currentTarget);
        setSelectedCustomer(customer);
    };

    const handleMenuClose = () => {
        console.log('Menu closed, selected customer:', selectedCustomer);
        setMenuAnchorEl(null);
    };

    const handleDeleteClick = () => {
        setCustomerToDelete(selectedCustomer);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setCustomerToDelete(null);
    };

    const handleDeleteCustomer = async () => {
        if (!customerToDelete) return;

        setDeleting(true);
        try {
            await axios.delete(`/api/partners/customers/${customerToDelete.id}`);
            setAlertMessage(`Customer "${customerToDelete.name}" deleted successfully`);
            setAlertSeverity('success');
            setShowAlert(true);
            fetchData(); // Refresh the list
        } catch (error) {
            console.error('Error deleting customer:', error);
            setAlertMessage(error.response?.data?.error || 'Error deleting customer');
            setAlertSeverity('error');
            setShowAlert(true);
        } finally {
            setDeleting(false);
            handleCloseDeleteDialog();
        }
    };

    const handleUsageInfoClick = (customer) => {
        setSelectedCustomerUsage(customer);
        setUsageDialogOpen(true);
    };

    const handleCloseUsageDialog = () => {
        setUsageDialogOpen(false);
        setSelectedCustomerUsage(null);
    };

    const handleResetPasswordClick = () => {
        setResetPasswordDialogOpen(true);
        handleMenuClose();
    };

    const handleCloseResetPasswordDialog = () => {
        setResetPasswordDialogOpen(false);
        setResetPasswordResult(null);
    };

    const handleResetPassword = async () => {
        if (!selectedCustomer) return;

        setResettingPassword(true);
        try {
            const response = await axios.post(`/api/partners/customers/${selectedCustomer.id}/reset-password`);
            setResetPasswordResult(response.data);
        } catch (error) {
            console.error('Error resetting password:', error);
            setAlertMessage(error.response?.data?.error || 'Error resetting password');
            setAlertSeverity('error');
            setShowAlert(true);
        } finally {
            setResettingPassword(false);
        }
    };

    const handleCopyPassword = async () => {
        if (resetPasswordResult?.password) {
            try {
                await navigator.clipboard.writeText(resetPasswordResult.password);
                setAlertMessage('Password copied to clipboard');
                setAlertSeverity('success');
                setShowAlert(true);
            } catch (error) {
                console.error('Error copying to clipboard:', error);
                setAlertMessage('Failed to copy password to clipboard');
                setAlertSeverity('error');
                setShowAlert(true);
            }
        }
    };

    const handleToggleDebugMode = async (customer) => {
        const newDebugMode = !customer.debugMode;
        setTogglingDebugMode(true);
        try {
            const response = await axios.post(`/api/partners/customers/${customer.id}/toggle-debug-mode`, {
                debugMode: newDebugMode
            });
            
            setAlertMessage(response.data.message);
            setAlertSeverity('success');
            setShowAlert(true);
            fetchData(); // Refresh the list to show updated debug mode status
        } catch (error) {
            console.error('Error toggling debug mode:', error);
            setAlertMessage(error.response?.data?.error || 'Error toggling debug mode');
            setAlertSeverity('error');
            setShowAlert(true);
        } finally {
            setTogglingDebugMode(false);
            handleMenuClose();
        }
    };

    const formatUsageValue = (value, type) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        
        if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : 'None';
        }
        
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        
        return value.toString();
    };

    const columns = [
        { field: "id", headerName: "ID", width: 70 },
        { field: "name", headerName: "Customer Name", width: 250 },
        { 
            field: "plan", 
            headerName: "Plan", 
            width: 200,
            valueGetter: (params) => {
                return params.row.plan?.name || 'Unknown';
            },
            renderCell: (params) => {
                const planName = params.row.plan?.name || 'Unknown';
                const noTrial = params.row.FreeTrialEligible === false;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip 
                            label={planName} 
                            size="small" 
                            color={planName === 'Unknown' ? 'default' : 'primary'}
                            variant="outlined"
                        />
                        {noTrial && (
                            <Tooltip title="Free trial disabled for this customer">
                                <Chip label="NT" size="small" color="error" variant="filled" sx={{ minWidth: 0, height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                            </Tooltip>
                        )}
                    </Box>
                );
            }
        },
        { 
            field: "debugMode", 
            headerName: "Debug", 
            width: 100,
            renderCell: (params) => {
                const debugMode = params.row.debugMode;
                return debugMode ? (
                    <Tooltip title="Debug Mode Enabled">
                        <Chip 
                            icon={<BugReportIcon />}
                            label="Debug" 
                            size="small" 
                            color="warning"
                            variant="filled"
                        />
                    </Tooltip>
                ) : null;
            }
        },
        { 
            field: "username", 
            headerName: "Username", 
            width: 200,
            valueGetter: (params) => {
                const customer = params.row;
                return generateUsername(customer);
            }
        },
        { 
            field: "createdAt", 
            headerName: "Created", 
            width: 180,
            valueGetter: (params) => {
                const date = new Date(params.value);
                return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
            }
        },
        { 
            field: 'actions', 
            headerName: 'Actions', 
            width: 150,
            renderCell: (params) => {
                const customer = params.row;
                const loginStatus = getLoginStatus(customer);
                
                return (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Usage Statistics">
                            <IconButton
                                onClick={() => handleUsageInfoClick(customer)}
                                size="small"
                                color="info"
                            >
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            onClick={(e) => handleMenuClick(e, params.row)}
                            size="small"
                        >
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl) && selectedCustomer?.id === customer.id}
                            onClose={handleMenuClose}
                        >
                            <MenuItem 
                                onClick={() => {
                                    handleMenuClose();
                                    handleLoginClick(customer);
                                }}
                                disabled={!loginStatus.canLogin}
                                sx={{
                                    opacity: loginStatus.canLogin ? 1 : 0.5,
                                    cursor: loginStatus.canLogin ? 'pointer' : 'not-allowed'
                                }}
                            >
                                <LoginIcon fontSize="small" sx={{ mr: 1 }} />
                                {loginStatus.canLogin ? 'Login' : 'Login (Access Revoked)'}
                            </MenuItem>
                            <MenuItem onClick={handleResetPasswordClick}>
                                <LockResetIcon fontSize="small" sx={{ mr: 1 }} />
                                Reset Password
                            </MenuItem>
                            <MenuItem 
                                onClick={() => handleToggleDebugMode(customer)}
                                disabled={togglingDebugMode}
                            >
                                <BugReportIcon fontSize="small" sx={{ mr: 1 }} />
                                {customer.debugMode ? 'Disable Debug Mode' : 'Enable Debug Mode'}
                            </MenuItem>
                            <MenuItem onClick={handleDeleteClick}>
                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                Delete
                            </MenuItem>
                        </Menu>
                    </Box>
                );
            },
        }
    ];
      
    return (
        <Layout title='Partner Customers'>
            <Box ref={filterRef} sx={{ mb: 2 }}>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6">
                            Manage Customer Accounts
                        </Typography>
                        {!loadingPartnerInfo && partnerInfo && !partnerInfo.isAuthorised && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <WarningIcon color="warning" fontSize="small" />
                                <Typography variant="body2" color="warning.main">
                                    Partner Not Verified - Customer creation disabled
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0 } }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateCustomer}
                            startIcon={<PersonAddIcon />}
                            disabled={loadingPartnerInfo || !partnerInfo?.isAuthorised}
                        >
                            Create New Customer
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {!loadingPartnerInfo && partnerInfo && !partnerInfo.isAuthorised && (
                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                Partner Verification Required
                            </Typography>
                            <Typography variant="body2">
                                Your partner account needs to be verified before you can create customer accounts. 
                                Please contact your referring partner to get verified, or reach out to support if you need assistance.
                            </Typography>
                            {partnerInfo.referringPartner && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Referring Partner:</strong> {partnerInfo.referringPartner.name}
                                </Typography>
                            )}
                        </Box>
                    </Alert>
                </Box>
            )}

            <Box>
                <div style={{ height: gridHeight, width: '100%' }}>
                    <DataGrid
                        loading={loading}
                        rows={rows}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        disableSelectionOnClick
                    />
                </div>
            </Box>

            {/* Usage Statistics Dialog */}
            <Dialog
                open={usageDialogOpen}
                onClose={handleCloseUsageDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <InfoIcon color="info" />
                        <Box>
                            <Typography variant="h6">
                                Usage Statistics - {selectedCustomerUsage?.name}
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary">
                                Plan: {selectedCustomerUsage?.plan?.name || 'Unknown'}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedCustomerUsage?.usage ? (
                        <Box>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Current Usage vs Plan Limits
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemText
                                        primary={`Kiosks: ${selectedCustomerUsage.usage.numberOfKiosks} / ${selectedCustomerUsage.plan?.NumberOfAllowedKiosks || 'Unlimited'}`}
                                        secondary="Number of kiosk devices configured"
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`Responders: ${selectedCustomerUsage.usage.numberOfResponders} / ${selectedCustomerUsage.plan?.NumberOfAllowedResponders || 'Unlimited'}`}
                                        secondary="Number of unique responders across all devices"
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`Aliases: ${selectedCustomerUsage.usage.numberOfAliases} / ${selectedCustomerUsage.plan?.NumberOfAllowedAliases || 'Unlimited'}`}
                                        secondary="Number of responder aliases configured"
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`ANPR Plates: ${selectedCustomerUsage.usage.ANPRAllowedPlates} / ${selectedCustomerUsage.plan?.ANPRAllowedPlates || 'Unlimited'}`}
                                        secondary="Number of ANPR plates configured"
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`QR Code Keys: ${selectedCustomerUsage.usage.QRCodeKeysAllowed} / ${selectedCustomerUsage.plan?.QRCodeKeysAllowed || 'Unlimited'}`}
                                        secondary="Number of QR code keys configured"
                                    />
                                </ListItem>
                            </List>

                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Feature Usage
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemText
                                        primary={`Advanced Kiosk Customisation: ${formatUsageValue(selectedCustomerUsage.usage.AdvancedKioskCustomisation)}`}
                                        secondary={`Allowed: ${selectedCustomerUsage.plan?.AdvancedKioskCustomisation ? 'Yes' : 'No'}`}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`ANPR Enabled: ${formatUsageValue(selectedCustomerUsage.usage.ANPREnabled)}`}
                                        secondary={`Devices using ANPR feature`}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`ANPR 2-Step: ${formatUsageValue(selectedCustomerUsage.usage.ANPR2Step)}`}
                                        secondary={`Allowed: ${selectedCustomerUsage.plan?.ANPR2Step ? 'Yes' : 'No'}`}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`Movement Video: ${formatUsageValue(selectedCustomerUsage.usage.MovementVideoEnabled)}`}
                                        secondary={`Allowed: ${selectedCustomerUsage.plan?.MovementVideoAllowed ? 'Yes' : 'No'}`}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`High Resolution: ${formatUsageValue(selectedCustomerUsage.usage.HighResEnabled)}`}
                                        secondary={`Allowed: ${selectedCustomerUsage.plan?.HighResAllowed ? 'Yes' : 'No'}`}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`Gate Opening: ${formatUsageValue(selectedCustomerUsage.usage.gateOpening)}`}
                                        secondary={`Allowed: ${selectedCustomerUsage.plan?.GateOpening ? 'Yes' : 'No'}`}
                                    />
                                </ListItem>
                            </List>

                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Plan Details
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemText
                                        primary={`Movement Detection Storage: ${selectedCustomerUsage.plan?.MovementDetectionDaysStorage || 'N/A'} days`}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`Log Storage: ${selectedCustomerUsage.plan?.LogDaysStorage || 'N/A'} days`}
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem>
                                    <ListItemText
                                        primary={`API Access: ${selectedCustomerUsage.plan?.APIAccess ? 'Yes' : 'No'}`}
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    ) : (
                        <Typography>No usage data available</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUsageDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Create Customer Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create New Customer</DialogTitle>
                <form onSubmit={handleSubmitCreate}>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Organisation Name"
                                    name="organisationName"
                                    value={form.organisationName}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={form.firstName}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={form.lastName}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={form.password}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    required
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.freeTrialEligible}
                                            onChange={(e) => setForm(prev => ({ ...prev, freeTrialEligible: e.target.checked }))}
                                            color="primary"
                                        />
                                    }
                                    label="Free trial eligible"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseCreateDialog}>Cancel</Button>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            color="primary"
                            disabled={creating}
                        >
                            {creating ? <CircularProgress size={24} /> : 'Create Customer'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Login Dialog */}
            <Dialog
                open={loginDialogOpen}
                onClose={handleCloseLoginDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Login to Customer Account</DialogTitle>
                <DialogContent>
                    {selectedCustomer && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body1">
                                <strong>Customer:</strong> {selectedCustomer.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Username:</strong> {generateUsername(selectedCustomer)}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        label="Password"
                        type={showLoginPassword ? 'text' : 'password'}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        variant="outlined"
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                                        edge="end"
                                    >
                                        {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLoginDialog}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={handleLogin}
                        disabled={loggingIn}
                    >
                        {loggingIn ? <CircularProgress size={24} /> : 'Login'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Delete Customer</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete {customerToDelete?.name}? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteCustomer}
                        variant="contained" 
                        color="error"
                        disabled={deleting}
                    >
                        {deleting ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog
                open={resetPasswordDialogOpen}
                onClose={handleCloseResetPasswordDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LockResetIcon color="warning" />
                        <Typography variant="h6">
                            Reset Password
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedCustomer && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body1">
                                <strong>Customer:</strong> {selectedCustomer.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Username:</strong> {generateUsername(selectedCustomer)}
                            </Typography>
                        </Box>
                    )}
                    
                    {!resetPasswordResult ? (
                        <Typography>
                            This will generate a new random password for the partner account. 
                            The new password will be displayed after the reset is complete.
                        </Typography>
                    ) : (
                        <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Password reset successful!
                            </Alert>
                            <Typography variant="h6" gutterBottom>
                                New Password:
                            </Typography>
                            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {resetPasswordResult.password}
                                </Typography>
                                <IconButton
                                    onClick={handleCopyPassword}
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1 }}
                                    title="Copy password to clipboard"
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Please take note of the password as it will not be shown again. Should you lose the password, you can simply reset it again.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseResetPasswordDialog}>
                        {resetPasswordResult ? 'Close' : 'Cancel'}
                    </Button>
                    {!resetPasswordResult && (
                        <Button 
                            onClick={handleResetPassword}
                            variant="contained" 
                            color="warning"
                            disabled={resettingPassword}
                        >
                            {resettingPassword ? <CircularProgress size={24} /> : 'Reset Password'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Alert Snackbar */}
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                open={showAlert}
                autoHideDuration={6000}
                onClose={() => setShowAlert(false)}
            >
                <Alert severity={alertSeverity} onClose={() => setShowAlert(false)}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Layout>
    );
}

export default PartnerCustomers; 